import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getPercentage } from '../../utils';
import Loading from '../Loading';
import { Link } from 'react-router-dom';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

const insightsCardInfo = [
  {
    title: 'Top 10 Developers',
    matchNameKey: 'authors',
    dataKey: 'top10Authors',
    path: '/developers',
    subtitle: 'Contributions summarized for the selected period',
    color: '#3B82F6'
  },
  {
    title: 'Top 10 Categories',
    matchNameKey: 'categories',
    dataKey: 'top10Categories',
    path: '/categories',
    subtitle: 'Activity summarized for the selected period',
    color: '#10B981'
  },
  {
    title: 'Top 10 Projects',
    matchNameKey: 'projects',
    dataKey: 'top10Projects',
    path: '/projects',
    subtitle: 'Project data summarized for the selected period',
    color: '#F59E0B'
  },
  {
    title: 'Top 10 Repositories',
    matchNameKey: 'repos',
    dataKey: 'top10Repos',
    path: '/repos',
    subtitle: 'Repository statistics for the selected period',
    color: '#EF4444'
  }
];

const Content = ({ contentData, loading, displayMode, isDarkMode }) => {
  const dashboardData = useSelector(state => state.userInfo.dashboardData);
  const [sortOrders, setSortOrders] = useState({});
  const isInvestment = displayMode === 'investment';

  const totalEffort = contentData?.cards?.totalEffort;

  const toggleSortOrder = key => {
    setSortOrders(prev => ({
      ...prev,
      [key]: prev[key] === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortSuffix = sortOrder => {
    return sortOrder === 'asc' ? 'Lowest' : 'Highest';
  };

  if (loading) {
    return (
      <div className="col-span-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-stroke bg-white'
            }`}
          >
            <Loading type="bar" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {insightsCardInfo.map((cardInfo, index) => {
        const {
          title,
          dataKey,
          path,
          subtitle,
          color,
          barChart = true
        } = cardInfo;
        const data = contentData?.[dataKey] || [];
        const labelKeys = dashboardData[cardInfo.matchNameKey];
        const sortOrder = sortOrders[dataKey] || 'desc';

        const sortedEntries = Object.entries(data)
          .sort(([, a], [, b]) => (sortOrder === 'asc' ? a - b : b - a))
          .slice(0, 10);

        if (barChart) {
          const categories = sortedEntries.map(
            ([key]) => labelKeys?.[key]?.label || key
          );
          const seriesData = sortedEntries.map(([, value]) => value);

          const options = {
            chart: {
              type: 'bar',
              backgroundColor: 'transparent',
              plotBorderWidth: 0,
              plotBorderColor: 'transparent'
            },
            exporting: {
              enabled: false
            },
            legend: {
              enabled: false
            },
            title: {
              text: ''
            },
            xAxis: {
              categories,
              title: {
                text: null
              },
              labels: {
                style: {
                  color: isDarkMode ? '#e2e8f0' : '#334155',
                  fontSize: '12px',
                  fontWeight: '500'
                }
              },
              lineWidth: 0,
              gridLineWidth: 0
            },
            yAxis: {
              min: 0,
              title: {
                text: '',
                align: 'high'
              },
              labels: {
                enabled: false
              },
              gridLineWidth: 0,
              lineWidth: 0,
              tickLength: 0
            },
            tooltip: {
              backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
              borderColor: isDarkMode ? '#334155' : '#cbd5e1',
              valueDecimals: 0,
              style: {
                color: isDarkMode ? '#e2e8f0' : '#0f172a'
              },
              valueSuffix: isInvestment ? ' $' : ' effort'
            },
            plotOptions: {
              bar: {
                pointWidth: 30,
                pointPadding: 0,
                groupPadding: 0,
                dataLabels: {
                  enabled: true,
                  style: {
                    color: isDarkMode ? '#f8fafc' : '#0f172a',
                    textOutline: 'none',
                    fontWeight: '700'
                  },
                  formatter: function () {
                    return Highcharts.numberFormat(this.y, 0, ',', ',');
                  }
                }
              }
            },
            credits: {
              enabled: false
            },
            series: [
              {
                name: isInvestment ? 'Estimated Cost Without AI ($)' : 'Effort',
                data: seriesData,
                color: color
              }
            ]
          };

          return (
            <div
              key={dataKey || index}
              className={`col-span-12 rounded-sm border p-4 shadow-default xl:col-span-6 ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <div className="mb-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <h3
                        className={`text-dm font-semibold ${isDarkMode ? 'text-white' : 'text-blue-600'}`}
                      >
                        <Link
                          to={path}
                          className="rounded-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-boxdark"
                        >
                          {title}
                        </Link>
                      </h3>
                      <Link
                        to={path}
                        aria-label={`View details for ${title}`}
                        className={`group inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                          isDarkMode
                            ? 'bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 focus-visible:ring-offset-boxdark'
                            : 'bg-blue-50 text-blue-700 hover:bg-blue-100 focus-visible:ring-offset-white'
                        }`}
                      >
                        <span>View details</span>
                        <ArrowForwardIcon
                          aria-hidden="true"
                          className="transition-transform group-hover:translate-x-0.5"
                          sx={{ fontSize: 14 }}
                        />
                      </Link>
                    </div>
                    {subtitle && (
                      <p
                        className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
                      >
                        {subtitle}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => toggleSortOrder(dataKey)}
                    aria-label={`Toggle sort order for ${title}`}
                    className="flex w-full items-center justify-center space-x-1 rounded-md px-2 py-1 text-sm font-medium text-gray-700 dark:text-gray-300 sm:w-auto
                 hover:bg-blue-100 dark:hover:bg-blue-700 focus:outline-none transition"
                  >
                    {sortOrder === 'asc' ? (
                      <ArrowUpwardIcon
                        className="h-5 w-5 text-red-500"
                        style={{ width: '1rem', height: '1rem' }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        className="h-5 w-5 text-green-500"
                        style={{ width: '1rem', height: '1rem' }}
                      />
                    )}
                    <span
                      className={
                        sortOrder === 'asc' ? 'text-red-600' : 'text-green-600'
                      }
                    >
                      {sortOrder === 'asc' ? 'Lowest' : 'Highest'}
                    </span>
                  </button>
                </div>
              </div>

              <HighchartsReact highcharts={Highcharts} options={options} />
            </div>
          );
        }

        // Diğer kartlar için mevcut listeyi göster
        return (
          <div
            key={dataKey || index}
            className={`col-span-12 rounded-sm border p-4 shadow-default xl:col-span-6 ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-stroke bg-white'
            }`}
          >
            <div className="mb-7 ">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <h3
                    className={`text-dm font-medium ${isDarkMode ? 'text-white' : 'text-blue-600'}`}
                  >
                    <Link
                      to={path}
                      className="rounded-sm hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-boxdark"
                    >
                      {title}
                    </Link>
                  </h3>
                  <Link
                    to={path}
                    aria-label={`View details for ${title}`}
                    className={`group inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                      isDarkMode
                        ? 'bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 focus-visible:ring-offset-boxdark'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 focus-visible:ring-offset-white'
                    }`}
                  >
                    <span>View details</span>
                    <ArrowForwardIcon
                      aria-hidden="true"
                      className="transition-transform group-hover:translate-x-0.5"
                      sx={{ fontSize: 14 }}
                    />
                  </Link>
                </div>

                <div className="sm:pl-5">
                  <button onClick={() => toggleSortOrder(dataKey)}>
                    {sortOrder === 'asc' ? (
                      <ArrowUpwardIcon
                        className="h-4 w-4 text-red-500"
                        style={{ width: '1rem', height: '1rem' }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        className="h-4 w-4 text-green-500"
                        style={{ width: '1rem', height: '1rem' }}
                      />
                    )}
                  </button>
                </div>
              </div>
              <div
                className={`text-xs font-normal w-full text-right ${
                  sortOrder === 'desc' ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {getSortSuffix(sortOrder)}
              </div>
            </div>

            <div className="space-y-2.5 ">
              {sortedEntries.map(([itemKey], itemIndex) => {
                const labelKeyName = labelKeys?.[itemKey]?.label || itemKey;
                const itemValue = getPercentage(data[itemKey], totalEffort);

                return (
                  <div
                    className="items-center sm:flex gap-2 w-full"
                    key={`${itemIndex}-div`}
                  >
                    <div
                      className="relative group flex items-center gap-3.5 text-sm"
                      style={{ minWidth: '100px', maxWidth: '100px' }}
                      aria-label={labelKeyName}
                    >
                      <p
                        className={`truncate whitespace-nowrap overflow-hidden cursor-help font-medium ${isDarkMode ? 'text-white' : 'text-black'}`}
                      >
                        {labelKeyName}
                      </p>

                      {/* Tooltip */}
                      <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-max max-w-xs -translate-x-1/2 rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 whitespace-nowrap z-50">
                        {labelKeyName}
                      </div>
                    </div>
                    <div className="relative block h-4.5 w-full rounded bg-meta-9 dark:bg-meta-4">
                      <div
                        className="h-full rounded bg-primary text-xs font-medium text-white flex items-center justify-center relative group cursor-pointer"
                        style={{ width: `${itemValue}%` }}
                      >
                        {/* Tooltip */}
                        <div className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-max max-w-xs -translate-x-1/2 rounded bg-black px-2 py-1 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100 whitespace-nowrap z-50">
                          Effort: {data[itemKey].toLocaleString()} ({itemValue}
                          %)
                        </div>
                      </div>
                    </div>
                    <div
                      className={`text-xs ${isDarkMode ? 'text-slate-200' : 'text-black'}`}
                      style={{ minWidth: '20px', maxWidth: '20px' }}
                    >
                      {itemValue}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </>
  );
};

Content.propTypes = {
  contentData: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  displayMode: PropTypes.string,
  isDarkMode: PropTypes.bool
};

export default Content;
